// =============================================================
// Palette engine.
//
// Every palette comes in a day and a night version (see MODES
// below). A palette moves hue and holds CONTRAST,
// which is what makes handing a colour picker to a user safe:
// text keeps the legibility it has today whatever is chosen.
//
// Contrast is held two different ways, and the difference matters:
//
//   SURFACES (near-white, dark ink on them) pin HSL lightness.
//   At 92-100% lightness hue barely moves luminance, so this is
//   both sufficient and exactly reproducible.
//
//   THE RAIL (dark, white text on it) pins LUMINANCE and solves
//   for lightness per hue. Pinning lightness there does NOT work:
//   a teal at L=44.7% is far brighter than the shipped indigo at
//   the same L, and white text on it measured 3.12:1.
//
// What a palette owns:  accent + surfaces + lines + the sidebar.
// What it never owns:   ink, and the status tints (present /
//                       absent / warning pills, avatar tones).
//                       Those are meaning, not decoration.
// =============================================================

// ── colour maths ────────────────────────────────────────────
// Small and local. A colour library for thirteen values would
// be more to install than to write.

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

export const hexToRgb = (hex) => {
  let h = String(hex || '').trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

export const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b]
    .map((v) => clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

export const rgbToHsl = ({ r, g, b }) => {
  const r1 = r / 255;
  const g1 = g / 255;
  const b1 = b / 255;
  const max = Math.max(r1, g1, b1);
  const min = Math.min(r1, g1, b1);
  const l = (max + min) / 2;
  const d = max - min;

  if (d === 0) return { h: 0, s: 0, l: l * 100 };

  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r1) h = ((g1 - b1) / d + (g1 < b1 ? 6 : 0)) / 6;
  else if (max === g1) h = ((b1 - r1) / d + 2) / 6;
  else h = ((r1 - g1) / d + 4) / 6;

  return { h: h * 360, s: s * 100, l: l * 100 };
};

export const hslToRgb = ({ h, s, l }) => {
  const h1 = ((h % 360) + 360) % 360 / 360;
  const s1 = clamp(s, 0, 100) / 100;
  const l1 = clamp(l, 0, 100) / 100;

  if (s1 === 0) {
    const v = Math.round(l1 * 255);
    return { r: v, g: v, b: v };
  }

  const q = l1 < 0.5 ? l1 * (1 + s1) : l1 + s1 - l1 * s1;
  const p = 2 * l1 - q;
  const channel = (t) => {
    let t1 = t;
    if (t1 < 0) t1 += 1;
    if (t1 > 1) t1 -= 1;
    if (t1 < 1 / 6) return p + (q - p) * 6 * t1;
    if (t1 < 1 / 2) return q;
    if (t1 < 2 / 3) return p + (q - p) * (2 / 3 - t1) * 6;
    return p;
  };

  return {
    r: Math.round(channel(h1 + 1 / 3) * 255),
    g: Math.round(channel(h1) * 255),
    b: Math.round(channel(h1 - 1 / 3) * 255),
  };
};

export const hexToHsl = (hex) => {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHsl(rgb) : null;
};

export const hslToHex = (hsl) => rgbToHex(hslToRgb(hsl));

// WCAG relative luminance, used to check a custom accent can
// carry white button text before it is accepted.
export const relativeLuminance = (hex) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const f = (v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * f(rgb.r) + 0.7152 * f(rgb.g) + 0.0722 * f(rgb.b);
};

export const contrastWithWhite = (hex) => {
  const l = relativeLuminance(hex);
  return Number((1.05 / (l + 0.05)).toFixed(2));
};

/** WCAG contrast between any two colours, lighter over darker. */
export const contrastRatio = (a, b) => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return Number(((hi + 0.05) / (lo + 0.05)).toFixed(2));
};

// ── the surface ladder ──────────────────────────────────────
// Lightness and the *relative* saturation of every surface,
// read off the palette the app ships with today (Classic).
// A palette supplies a hue and a tint strength; these numbers
// never change, which is what pins the contrast.

const SURFACE_LADDER = {
  ground: { l: 98.4, s: 46 },
  card: { l: 100, s: 0 },
  tint: { l: 99.0, s: 60 },
  zebra: { l: 97.8, s: 42 },
  sunken: { l: 95.3, s: 31 },
  line: { l: 92.5, s: 27 },
  lineSoft: { l: 94.7, s: 31 },
  lineFaint: { l: 93.3, s: 29 },
};

// Accent derivatives, as offsets from the picked accent. Read
// off the Classic pair: #3B6FF0 -> #3160DA hover, -> #5B4FE0
// gradient partner, -> #EEF3FE selected, -> #EEF3FB hover tint.
const ACCENT_HOVER = { dh: 0, ds: -16, dl: -7 };
// A darker version of the same hue, not a hue shift. Classic's
// partner happens to be a violet (#5B4FE0 beside #3B6FF0), but
// rotating the hue only flatters blues -- the same +22 turns a
// burnt orange into olive. Classic keeps its literal pair below;
// everything derived darkens instead, which is safe at any hue.
const ACCENT_DEEP = { dh: 0, ds: -10, dl: -14 };
const ACCENT_SOFT = { l: 96.5, sCap: 90, sFloor: 40 };
const SURFACE_HOVER = { l: 95.9, sCap: 80, sFloor: 35 };

// ── the rail ladder ─────────────────────────────────────────
// The sidebar is a dark gradient and stays one: only its hue
// follows the palette. White nav text sits on it, so what has to
// hold constant is the contrast under that text.
//
// NOTE, and it is the whole reason this section is not written
// the way the surfaces above are: HSL lightness is not perceptual
// luminance. Holding L at 44.7% across hues gives a teal that is
// far brighter than the shipped indigo -- measured, white text on
// it fell to 3.12:1, and on a fixed-L active pill to 1.86:1. The
// surfaces get away with pinning L only because they sit at
// 92-100% where hue barely moves luminance.
//
// So these targets are LUMINANCES, sampled from the shipped rail
// and pill, and the lightness that hits them is solved per hue.
const RAIL_LADDER = [
  { stop: 0, s: 52.6, from: '#3E36AE' },
  { stop: 46, s: 50.9, from: '#2A287B' },
  { stop: 100, s: 47.4, from: '#0F172A' },
];

// The active menu pill. A sweep rather than a flat fill, so it
// takes two stops; the second is rotated off the first the way
// the shipped indigo -> sky pair is, but by less, because a big
// rotation that flatters blue turns orange into yellow.
const NAV_ACTIVE = [
  { dh: 0, s: 73.7, from: '#4653E3' },
  { dh: -18, s: 76.5, from: '#1D91DA' },
];

// The muted section label on the rail, e.g. "MAIN MENU".
const SIDEBAR_LABEL = { s: 37.6, from: '#8891C9' };

/**
 * The HSL lightness that puts hue `h` at saturation `s` on the
 * same perceptual luminance as `target`. Luminance rises
 * monotonically with L at fixed h/s, so a bisection is exact
 * enough in a couple of dozen steps and needs no colour science
 * beyond the WCAG formula already above.
 */
const lightnessForLuminance = (h, s, target) => {
  let lo = 0;
  let hi = 100;
  for (let i = 0; i < 24; i += 1) {
    const mid = (lo + hi) / 2;
    if (relativeLuminance(hslToHex({ h, s, l: mid })) < target) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
};

/** Recolour `from` to hue `h` at saturation `s`, luminance held. */
const rehue = (from, h, s) =>
  hslToHex({ h, s, l: lightnessForLuminance(h, s, relativeLuminance(from)) });

// Shell values are composed strings (two of them gradients), so
// they are built rather than mapped, and are kept apart from the
// flat colour tokens above.
export const SHELL_TOKEN_NAMES = [
  '--rail',
  '--rail-top',
  '--nav-active',
  '--sidebar-bg',
  '--sidebar-label',
];

/** Build the sidebar's values from the same accent. */
export const buildShell = (accentHex) => {
  const accent = hexToHsl(accentHex);
  if (!accent) return null;

  const railHexes = RAIL_LADDER.map(({ s, from }) => rehue(from, accent.h, s));
  const railStops = RAIL_LADDER.map(
    ({ stop }, i) => `${railHexes[i]} ${stop}%`,
  ).join(', ');

  const navStops = NAV_ACTIVE.map(({ dh, s, from }) =>
    rehue(from, accent.h + dh, s),
  ).join(', ');

  return {
    '--rail': `linear-gradient(180deg, ${railStops})`,
    // The gradient's first stop as a flat colour. The brand head is
    // sticky inside the scroller, so it needs something opaque of its
    // own to hide the nav items passing under it -- and it sits at the
    // very top of the rail, which is exactly this stop.
    '--rail-top': railHexes[0],
    '--nav-active': `linear-gradient(90deg, ${navStops})`,
    // The flat fill behind the rail where a gradient would not
    // reach -- matches the middle stop.
    '--sidebar-bg': rehue(RAIL_LADDER[1].from, accent.h, RAIL_LADDER[1].s),
    '--sidebar-label': rehue(SIDEBAR_LABEL.from, accent.h, SIDEBAR_LABEL.s),
  };
};

// The thirteen custom properties a palette sets. Nothing else
// in the stylesheets is allowed to move with the theme.
export const TOKEN_NAMES = [
  '--pg-accent',
  '--pg-accent-hover',
  '--pg-accent-deep',
  '--pg-accent-soft',
  '--pg-ground',
  '--pg-card',
  '--pg-tint',
  '--pg-zebra',
  '--pg-sunken',
  '--pg-hover',
  '--pg-line',
  '--pg-line-soft',
  '--pg-line-faint',
];

/**
 * Build the full token set from one accent colour and a tint
 * strength (0 = neutral grey surfaces, 100 = surfaces carrying
 * the accent's hue as strongly as Classic does).
 */
export const buildTokens = (accentHex, tint = 100) => {
  const accent = hexToHsl(accentHex);
  if (!accent) return null;

  const t = clamp(tint, 0, 100) / 100;
  const surface = (key) => {
    const { l, s } = SURFACE_LADDER[key];
    return hslToHex({ h: accent.h, s: s * t, l });
  };

  return {
    '--pg-accent': rgbToHex(hexToRgb(accentHex)),
    '--pg-accent-hover': hslToHex({
      h: accent.h + ACCENT_HOVER.dh,
      s: clamp(accent.s + ACCENT_HOVER.ds, 0, 100),
      l: clamp(accent.l + ACCENT_HOVER.dl, 0, 100),
    }),
    '--pg-accent-deep': hslToHex({
      h: accent.h + ACCENT_DEEP.dh,
      s: clamp(accent.s + ACCENT_DEEP.ds, 0, 100),
      l: clamp(accent.l + ACCENT_DEEP.dl, 0, 100),
    }),
    '--pg-accent-soft': hslToHex({
      h: accent.h,
      s: clamp(accent.s, ACCENT_SOFT.sFloor, ACCENT_SOFT.sCap),
      l: ACCENT_SOFT.l,
    }),
    '--pg-ground': surface('ground'),
    '--pg-card': surface('card'),
    '--pg-tint': surface('tint'),
    '--pg-zebra': surface('zebra'),
    '--pg-sunken': surface('sunken'),
    '--pg-hover': hslToHex({
      h: accent.h,
      s: clamp(accent.s * 0.85, SURFACE_HOVER.sFloor, SURFACE_HOVER.sCap) * t,
      l: SURFACE_HOVER.l,
    }),
    '--pg-line': surface('line'),
    '--pg-line-soft': surface('lineSoft'),
    '--pg-line-faint': surface('lineFaint'),
  };
};

// ── modes ───────────────────────────────────────────────────
// Day and night are a second axis beside the palette, not a fifth
// palette: every palette -- the four prebuilt and any custom one --
// has a day version and a night version. Day is exactly today's
// values; night is derived here.
//
// What a MODE owns (css/theme/modes.css): ink, and the text that
// sits on a filled accent. Fixed per mode, never palette-tunable, so
// a user mixing a palette still cannot make text illegible.
// What a PALETTE owns, in each mode: accent + surfaces + lines. The
// rail is dark in both modes and does not change.

export const MODES = ['day', 'night'];
export const DEFAULT_MODE = 'day';

/* Night mode ships in phases. Until the shared page kit (tables,
   popups, filter cards, toasts) reads the mode's ink, a night page
   would put dark literal ink on dark surfaces -- so the switch stays
   hidden and a stored 'night' is held at day until this is true.

   Whoever is doing that conversion can see night in their own browser:
   localStorage.setItem('lms_ui_mode_preview', '1'), then reload. */
export const NIGHT_MODE_READY = false;

// ── the night surface ladder ────────────────────────────────
// A dark ground cannot pin lightness -- the rail ladder above is why --
// so night surfaces are LUMINANCE targets, solved per hue exactly as
// the rail is. Raised things are lighter, which is how a dark
// interface shows elevation; lines are lighter again so a hairline
// still reads. Saturation scales with tint, as the day ladder's does.
const NIGHT_SURFACE_LADDER = {
  ground: { lum: 0.0085, s: 30 },
  card: { lum: 0.016, s: 22 },
  tint: { lum: 0.02, s: 26 },
  zebra: { lum: 0.022, s: 20 },
  sunken: { lum: 0.03, s: 20 },
  hover: { lum: 0.042, s: 30 },
  line: { lum: 0.068, s: 16 },
  lineSoft: { lum: 0.046, s: 16 },
  lineFaint: { lum: 0.056, s: 16 },
};

// At night the accent does two jobs at once -- a fill under
// --on-accent text, and text on a dark card -- so it is pinned to a
// luminance that serves both: light enough to read on the card, with
// the mode's dark --on-accent text readable on the fill.
const NIGHT_ACCENT = { lum: 0.26, sFloor: 45, sCap: 90 };
const NIGHT_ACCENT_HOVER = { lum: 0.34 };
const NIGHT_ACCENT_DEEP = { lum: 0.17 };
const NIGHT_ACCENT_SOFT = { lum: 0.035, sCap: 60 };

const atLuminance = (h, s, lum) => hslToHex({ h, s, l: lightnessForLuminance(h, s, lum) });

/**
 * The night token set for an accent and tint strength -- the same
 * thirteen names as buildTokens, so nothing that reads them changes.
 */
export const buildNightTokens = (accentHex, tint = 100) => {
  const accent = hexToHsl(accentHex);
  if (!accent) return null;

  const t = clamp(tint, 0, 100) / 100;
  const surface = (key) => {
    const { lum, s } = NIGHT_SURFACE_LADDER[key];
    return atLuminance(accent.h, s * t, lum);
  };
  const accentS = clamp(accent.s, NIGHT_ACCENT.sFloor, NIGHT_ACCENT.sCap);

  return {
    '--pg-accent': atLuminance(accent.h, accentS, NIGHT_ACCENT.lum),
    '--pg-accent-hover': atLuminance(accent.h, accentS, NIGHT_ACCENT_HOVER.lum),
    '--pg-accent-deep': atLuminance(accent.h, accentS, NIGHT_ACCENT_DEEP.lum),
    '--pg-accent-soft': atLuminance(
      accent.h,
      Math.min(accentS, NIGHT_ACCENT_SOFT.sCap),
      NIGHT_ACCENT_SOFT.lum,
    ),
    '--pg-ground': surface('ground'),
    '--pg-card': surface('card'),
    '--pg-tint': surface('tint'),
    '--pg-zebra': surface('zebra'),
    '--pg-sunken': surface('sunken'),
    '--pg-hover': surface('hover'),
    '--pg-line': surface('line'),
    '--pg-line-soft': surface('lineSoft'),
    '--pg-line-faint': surface('lineFaint'),
  };
};

// ── the prebuilt palettes ───────────────────────────────────
// Classic reproduces today's page exactly -- it is the default
// so that shipping this changes nothing until someone chooses
// otherwise. The other three differ in hue and tint strength
// only, so all four have identical contrast.

// Each prebuilt palette carries its finished values rather than
// deriving them at runtime. Two reasons: Classic has to
// reproduce today's stylesheet exactly (a round-trip through HSL
// moves seven of the thirteen by a digit, which would mean
// shipping this changed the page), and a named palette is a
// designed thing that should be tunable by hand afterwards.
// buildTokens() is still what generated these, and is what the
// custom picker uses live.

export const PALETTES = [
  {
    id: 'classic',
    name: 'Classic',
    blurb: 'The blue the LMS ships with. Cool, neutral, familiar.',
    accent: '#3B6FF0',
    tint: 100,
    // Verbatim from TrainerDashboard.css as it stood before the
    // palette work. Do not regenerate these.
    tokens: {
      '--pg-accent': '#3B6FF0',
      '--pg-accent-hover': '#3160DA',
      '--pg-accent-deep': '#5B4FE0',
      '--pg-accent-soft': '#EEF3FE',
      '--pg-ground': '#F9FBFC',
      '--pg-card': '#FFFFFF',
      '--pg-tint': '#FBFCFE',
      '--pg-zebra': '#F7F9FC',
      '--pg-sunken': '#EFF2F7',
      '--pg-hover': '#EEF3FB',
      '--pg-line': '#E6EAF2',
      '--pg-line-soft': '#EDF0F6',
      '--pg-line-faint': '#E9EDF3',
    },
    // The rail exactly as shipped. Re-deriving it at Classic's own
    // accent hue lands on #2B4689 rather than #3E36AE -- the same
    // luminance, a different colour -- so like the surfaces, these
    // are literals. Do not regenerate.
    shell: {
      '--rail': 'linear-gradient(180deg, #3E36AE 0%, #2A287B 46%, #0F172A 100%)',
      '--rail-top': '#3E36AE',
      '--nav-active': 'linear-gradient(90deg, #4653E3, #1D91DA)',
      '--sidebar-bg': '#2A287B',
      '--sidebar-label': '#8891C9',
    },
  },
  {
    id: 'premium',
    name: 'Premium',
    blurb: 'Deep violet on near-neutral paper. Quiet, and the surfaces stay almost grey.',
    accent: '#5A4BC4',
    tint: 45,
    tokens: {
      '--pg-accent': '#5A4BC4',
      '--pg-accent-hover': '#574D9E',
      '--pg-accent-deep': '#453B8C',
      '--pg-accent-soft': '#F3F2FB',
      '--pg-ground': '#FAFAFC',
      '--pg-card': '#FFFFFF',
      '--pg-tint': '#FCFCFD',
      '--pg-zebra': '#F9F8FA',
      '--pg-sunken': '#F2F1F5',
      '--pg-hover': '#F3F3F7',
      '--pg-line': '#EAEAEE',
      '--pg-line-soft': '#F0F0F3',
      '--pg-line-faint': '#ECECF0',
    },
    shell: {
      '--rail': 'linear-gradient(180deg, #4435AB 0%, #312777 46%, #171334 100%)',
      '--rail-top': '#4435AB',
      '--nav-active': 'linear-gradient(90deg, #5E4BE4, #6D84EC)',
      '--sidebar-bg': '#312777',
      '--sidebar-label': '#948DCB',
    },
  },
  {
    id: 'calm',
    name: 'Calm',
    blurb: 'Muted teal over cool paper. The gentlest accent of the four.',
    accent: '#17808F',
    tint: 80,
    tokens: {
      '--pg-accent': '#17808F',
      '--pg-accent-hover': '#1C5D66',
      '--pg-accent-deep': '#12454D',
      '--pg-accent-soft': '#F0FBFD',
      '--pg-ground': '#F9FCFC',
      '--pg-card': '#FFFFFF',
      '--pg-tint': '#FBFDFE',
      '--pg-zebra': '#F8FBFB',
      '--pg-sunken': '#F0F5F6',
      '--pg-hover': '#EFF8FA',
      '--pg-line': '#E8EFF0',
      '--pg-line-soft': '#EEF4F5',
      '--pg-line-faint': '#EAF1F2',
    },
    shell: {
      '--rail': 'linear-gradient(180deg, #1B5058 0%, #143A3F 46%, #0A1A1C 100%)',
      '--rail-top': '#1B5058',
      '--nav-active': 'linear-gradient(90deg, #13707D, #159C84)',
      '--sidebar-bg': '#143A3F',
      '--sidebar-label': '#4EA0AC',
    },
  },
  {
    id: 'warm',
    name: 'Warm',
    blurb: 'Burnt saffron over cream. The brand colour, turned down for all-day use.',
    accent: '#B35A16',
    tint: 85,
    tokens: {
      '--pg-accent': '#B35A16',
      '--pg-accent-hover': '#864C1F',
      '--pg-accent-deep': '#6D3B15',
      '--pg-accent-soft': '#FDF5EF',
      '--pg-ground': '#FDFBF9',
      '--pg-card': '#FFFFFF',
      '--pg-tint': '#FEFCFB',
      '--pg-zebra': '#FBF9F7',
      '--pg-sunken': '#F6F3F0',
      '--pg-hover': '#FAF4EF',
      '--pg-line': '#F0EBE7',
      '--pg-line-soft': '#F5F1EE',
      '--pg-line-faint': '#F2EDEA',
    },
    shell: {
      '--rail': 'linear-gradient(180deg, #694020 0%, #4A2D18 46%, #20150C 100%)',
      '--rail-top': '#694020',
      '--nav-active': 'linear-gradient(90deg, #9C5118, #E75E4A)',
      '--sidebar-bg': '#4A2D18',
      '--sidebar-label': '#BA8A66',
    },
  },
];

export const DEFAULT_PALETTE_ID = 'classic';

export const getPalette = (id) =>
  PALETTES.find((p) => p.id === id) || PALETTES.find((p) => p.id === DEFAULT_PALETTE_ID);

/**
 * Resolve any stored selection -- prebuilt or custom -- to tokens.
 * A prebuilt palette hands back its finished map; only a custom
 * one is derived, and it falls back to the default if the stored
 * accent is unreadable (hand-edited storage, a truncated write).
 */
export const resolveTokens = (selection, mode = DEFAULT_MODE) => {
  if (mode === 'night') {
    /* Night is always derived, prebuilt palettes included: there is no
       shipped night page to reproduce byte for byte, which is the only
       reason day's prebuilt maps are literals. */
    const source = selection && selection.id === 'custom'
      ? selection
      : getPalette(selection && selection.id);
    const built = buildNightTokens(source.accent, source.tint);
    if (built) return built;
    const fallback = getPalette(DEFAULT_PALETTE_ID);
    return buildNightTokens(fallback.accent, fallback.tint);
  }
  if (selection && selection.id === 'custom') {
    const built = buildTokens(selection.accent, selection.tint);
    if (built) return built;
  }
  return getPalette(selection && selection.id).tokens;
};

/** The same, for the sidebar's four composed values. */
export const resolveShell = (selection) => {
  if (selection && selection.id === 'custom') {
    const built = buildShell(selection.accent);
    if (built) return built;
  }
  return getPalette(selection && selection.id).shell;
};

// The canvas behind the pages is painted by the older --ground /
// --paper pair in css/theme/tokens.css. Point them at the palette
// so the page ground tints too, rather than leaving cards floating
// on a fixed grey. Written inline alongside the --pg-* set, which
// is what keeps this independent of stylesheet import order.
const MIRRORS = {
  '--ground': '--pg-ground',
  '--paper': '--pg-ground',
};

/** Paint a token set, and optionally the shell, onto :root. */
export const applyTokens = (tokens, shell) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  if (tokens) {
    TOKEN_NAMES.forEach((name) => {
      if (tokens[name]) root.style.setProperty(name, tokens[name]);
    });
    Object.entries(MIRRORS).forEach(([target, source]) => {
      if (tokens[source]) root.style.setProperty(target, tokens[source]);
    });
  }

  // The sidebar's values are written under the names the shell
  // stylesheets already consume (--rail, --nav-active, …) rather
  // than under --pg-* ones, so sidebar.css and shell.css needed no
  // edits at all -- and every `!important` in them still wins,
  // because what changed is the variable, not the rule.
  if (shell) {
    SHELL_TOKEN_NAMES.forEach((name) => {
      if (shell[name]) root.style.setProperty(name, shell[name]);
    });
  }
};

// ── storage adapter ─────────────────────────────────────────
// Every read and write of the user's choice goes through this.
// Swapping localStorage for a per-user API call later is a
// change to these three functions and nothing else.

const STORAGE_KEY = 'lms_ui_palette';
// What the old three-skin picker wrote. Read once, to migrate.
const LEGACY_KEY = 'lms_ui_theme';

export const paletteStore = {
  read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.id === 'string') return parsed;
      }
      // Nothing saved under the new key. Anyone still carrying
      // 'dark' or 'indigo' from the retired skins lands on the
      // default rather than on a theme that no longer exists.
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) {
        localStorage.removeItem(LEGACY_KEY);
        return { id: DEFAULT_PALETTE_ID };
      }
    } catch (error) {
      console.warn('Unable to read palette from storage:', error);
    }
    return { id: DEFAULT_PALETTE_ID };
  },

  write(selection) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selection));
    } catch (error) {
      console.warn('Unable to save palette to storage:', error);
    }
  },

  clear() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Unable to clear palette from storage:', error);
    }
  },
};

// ── the mode's storage and paint ────────────────────────────
// Kept apart from paletteStore: resetting the palette must not throw a
// user out of night mode, and the two are chosen independently.

const MODE_KEY = 'lms_ui_mode';
const MODE_PREVIEW_KEY = 'lms_ui_mode_preview';

export const modeStore = {
  read() {
    try {
      const stored = localStorage.getItem(MODE_KEY);
      return MODES.includes(stored) ? stored : DEFAULT_MODE;
    } catch (error) {
      console.warn('Unable to read mode from storage:', error);
      return DEFAULT_MODE;
    }
  },

  write(mode) {
    try {
      localStorage.setItem(MODE_KEY, mode);
    } catch (error) {
      console.warn('Unable to save mode to storage:', error);
    }
  },

  /** The developer unlock described at NIGHT_MODE_READY. */
  previewUnlocked() {
    try {
      return localStorage.getItem(MODE_PREVIEW_KEY) === '1';
    } catch (error) {
      return false;
    }
  },
};

/** Whether night may be shown in this browser. */
export const nightModeAvailable = () => NIGHT_MODE_READY || modeStore.previewUnlocked();

/**
 * Put the mode on <html>, where css/theme/modes.css reads it. The
 * surfaces for that mode are painted by applyTokens, separately.
 */
export const applyMode = (mode) => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-mode', mode === 'night' ? 'night' : 'day');
};
